import { render } from "preact";
import {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "preact/hooks";
import type { HostEntry, SortKey, HealthFilter } from "./types";
import {
  apiHosts,
  apiAdjust,
  apiPoll,
} from "./api";
import { Header } from "./components/Header";
import { BrowseBar } from "./components/BrowseBar";
import { Toolbar } from "./components/Toolbar";
import { HostRow } from "./components/HostRow";
import { SettingsModal } from "./components/SettingsModal";
import { SettingsPage } from "./components/SettingsPage";
import { DataSourcePage } from "./components/DataSourcePage";
import { ToastHost, useToasts } from "./components/Toast";
import { DonationBanner } from "./components/DonationBanner";
import { setupTipEngine } from "./lib/tip-engine";
import { withLocalBias } from "./lib/boost";
import { navigate } from "./lib/nav";
import {
  getPreferredInstance, setPreferredInstance,
  getRedirectX, setRedirectX,
  getCustomSource,
  setCustomSource,
  addMyBias,
  getMyBias,
  type MyBiasMap,
} from "./settings";
import "./styles.css";

/** Trivial client-side router: /data-source and /settings get their own pages; everything else renders the dashboard. */
function useRoute(): string {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return path;
}

/** Merge fresh host data while preserving the current visible order —
 *  used by silent background refreshes while an animated reorder is
 *  pending. Rows that disappeared upstream are dropped; new ones are
 *  appended at the end. */
function mergeKeepingOrder(prev: HostEntry[], next: HostEntry[]): HostEntry[] {
  const byDomain = new Map(next.map((h) => [h.domain, h]));
  const kept = prev
    .map((h) => byDomain.get(h.domain))
    .filter((h): h is HostEntry => h != null);
  const seen = new Set(kept.map((h) => h.domain));
  for (const h of next) if (!seen.has(h.domain)) kept.push(h);
  return kept;
}

function App() {
    const route = useRoute();
// in App(), before the /data-source check:
    if (route.startsWith("/v/") && route !== "/v/") { return <VRouteBounce rest={route.slice(2)} />; }

    if (route === "/data-source" || route.startsWith("/data-source/")) {
    return <DataSourcePage />;
  }
  if (route === "/settings" || route.startsWith("/settings/")) {
    return <SettingsPage />;
  }
  return <Dashboard />;
}
function VRouteBounce({ rest }: { rest: string }) {
    useEffect(() => {
        const search: string = window.location.search;
        const startAt: number = rest[0] === '/' ? 1 : 0;
        const qs: string = "/v/?path=" + encodeURIComponent(rest.substring(startAt));
        window.location.replace(search ? qs + "&" + search.slice(1) : qs);
    }, [rest]);
    return null;
}
function Dashboard() {
  const [hosts, setHosts] = useState<HostEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastPoll, setLastPoll] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<HealthFilter>("all");
  const [sort, setSort] = useState<SortKey>("effective");
  const [onlyRss, setOnlyRss] = useState(false);

  // Browser-stored state (localStorage-backed)
  const [preferred, setPreferred] = useState<string | null>(
    getPreferredInstance,
  );
  const [myBias, setMyBias] = useState<MyBiasMap>(getMyBias);
  const [redirectX, setRedirectXState] = useState<boolean>(getRedirectX);
  const [customSource, setCustomSourceState] = useState<string>(
    getCustomSource,
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  const timer = useRef<number | null>(null);
  const rowsRef = useRef<HTMLDivElement | null>(null);

  // Transient feedback (same toast host the standalone /settings page uses).
  // Boost/Penalize deliberately wait this long before the list reorders, so
  // the user can read the toast and then watch the row glide to its new
  // position (see scheduleReorder + the FLIP effect).
  const TOAST_MS = 3500;
  const { toasts, pushToast } = useToasts(TOAST_MS);

  // One-time tooltips with progress-bar acknowledgement. The engine is the
  // SAME module the /view banner uses — one implementation, one behaviour
  // (Ctrl/Shift pause, Alt reference mode, always-visible hint).
  useEffect(() => setupTipEngine(), []);

  // Re-read every localStorage-derived value — called after the settings UI
  // resets individual keys or clears all storage, so the dashboard never
  // shows state that no longer exists.
  const syncFromStorage = useCallback(() => {
    setPreferred(getPreferredInstance());
    setMyBias(getMyBias());
    setRedirectXState(getRedirectX());
    setCustomSourceState(getCustomSource());
  }, []);

  const refresh = useCallback(
    async (silent = false) => {
      if (!silent) setRefreshing(true);
      setError(null);
      try {
        // Merge the locally stored boost/preference scores into every entry
        // (personal floor/ceiling on bias, recomputed effective — see
        // src/lib/boost.ts) so the effective sort, the priority bars and the
        // browse suggestions all honor the user's own nudges, not just the
        // remotely shared value.
        const merged = (await apiHosts()).map(withLocalBias);
        // A Boost/Penalize reorder is deliberately pending (waiting out
        // the toast). A background refresh must not jump the gun and
        // re-sort silently — merge the fresh data but keep the current
        // order until the animated reorder fires.
        setHosts((prev) =>
          reorderTimer.current ? mergeKeepingOrder(prev, merged) : merged,
        );
        setLastPoll(new Date());
      } catch (err) {
        setError((err as Error).message);
        pushToast({ kind: "error", text: (err as Error).message });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [pushToast],
  );

  const handlePoll = useCallback(async () => {
    setRefreshing(true);
    try {
      const src = getCustomSource();
      const result = await apiPoll(src ? { sourceUrl: src } : {});
      pushToast({ kind: "success", text: `Polled ${result.updated} hosts${src ? " from your custom source" : ""}.` });
      await refresh(true);
    } catch (err) {
      pushToast({ kind: "error", text: (err as Error).message });
    } finally {
      setRefreshing(false);
    }
  }, [pushToast, refresh]);

  // Shared sort comparator — used both for the visible list and for the
  // delayed reorder after Boost/Penalize, so the two can never disagree.
  const compareHosts = useCallback((a: HostEntry, b: HostEntry) => {
    switch (sort) {
      case "effective":
        return b.effective - a.effective;
      case "score":
        return b.score - a.score;
      case "ping_avg":
        return (
          (a.ping_avg ?? Number.MAX_SAFE_INTEGER) -
          (b.ping_avg ?? Number.MAX_SAFE_INTEGER)
        );
      case "samples":
        return b.samples - a.samples;
      case "domain":
        return a.domain.localeCompare(b.domain);
      case "updated":
        return new Date(b.updated).getTime() - new Date(a.updated).getTime();
      default:
        return 0;
    }
  }, [sort]);

  // --- Animated reorder after Boost / Penalize -----------------------------
  // `visible` is derived (filter + sort), so it would re-sort instantly on
  // every hosts update. To keep the list visually still while the toast is
  // showing, the current display order is frozen at click time; after the
  // toast duration the freeze is lifted and every row animates from its
  // old position to its new one (FLIP: measure First, apply the layout
  // change, Invert with a transform, then Play the transition).
  const pendingFlip = useRef<Map<string, number> | null>(null);
  const reorderTimer = useRef<number | null>(null);
  const [frozenOrder, setFrozenOrder] = useState<string[] | null>(null);

  const cancelReorder = useCallback(() => {
    if (reorderTimer.current) {
      window.clearTimeout(reorderTimer.current);
      reorderTimer.current = null;
    }
    pendingFlip.current = null;
    setFrozenOrder(null);
  }, []);

  const scheduleReorder = useCallback(() => {
    if (reorderTimer.current) window.clearTimeout(reorderTimer.current);
    // Freeze the CURRENT DOM order (pre-adjustment — the in-place score
    // update has not committed yet) so nothing moves while the toast is up.
    setFrozenOrder(
      rowsRef.current
        ? [...rowsRef.current.querySelectorAll(".row")].map(
            (r) => (r as HTMLElement).dataset.domain!,
          )
        : null,
    );
    reorderTimer.current = window.setTimeout(() => {
      reorderTimer.current = null;
      const container = rowsRef.current;
      if (container) {
        const positions = new Map<string, number>();
        container
          .querySelectorAll<HTMLElement>(".row[data-domain]")
          .forEach((el) => {
            positions.set(el.dataset.domain!, el.getBoundingClientRect().top);
          });
        pendingFlip.current = positions;
      }
      // Lift the freeze and normalize `hosts` — the commit re-sorts the
      // visible list and the FLIP effect below plays the movement.
      setFrozenOrder(null);
      setHosts((cur) => [...cur].sort(compareHosts));
    }, TOAST_MS);
  }, [compareHosts]);

  // Runs synchronously after each hosts commit: if a reorder is pending,
  // measure the new positions and play the rows back into place.
  useLayoutEffect(() => {
    const prevTops = pendingFlip.current;
    if (!prevTops || prevTops.size === 0) return;
    pendingFlip.current = null;
    const container = rowsRef.current;
    if (!container) return;
    container
      .querySelectorAll<HTMLElement>(".row[data-domain]")
      .forEach((el) => {
        const prevTop = prevTops.get(el.dataset.domain!);
        if (prevTop == null) return;
        const dy = prevTop - el.getBoundingClientRect().top;
        if (Math.abs(dy) < 1) return;
        el.style.transition = "none";
        el.style.transform = `translateY(${dy}px)`;
        el.style.zIndex = "1";
        requestAnimationFrame(() => {
          el.style.transition = "transform .55s cubic-bezier(.22,1,.36,1)";
          el.style.transform = "";
          el.classList.add("row--moving");
          const cleanup = () => {
            el.style.zIndex = "";
            el.classList.remove("row--moving");
          };
          el.addEventListener("transitionend", cleanup, { once: true });
          window.setTimeout(cleanup, 700); // fallback
        });
      });
  }, [hosts]);

  const handleAdjust = useCallback(
    async (domain: string, delta: number) => {
      try {
        const updated = await apiAdjust(domain, delta);
        // Freeze the pre-adjustment display order FIRST (the DOM still
        // shows the old scores), then update the row in place — do NOT
        // sort yet. The reorder happens after the toast duration with a
        // FLIP animation, so the user can follow which row goes where.
        scheduleReorder();
        // Record the personal tally BEFORE merging: withLocalBias reads the
        // local record, and the fresh delta sets the personal floor/ceiling
        // on the displayed priority (see src/lib/boost.ts).
        const total = addMyBias(domain, delta);
        setMyBias(getMyBias());
        const merged = withLocalBias(updated);
        setHosts((cur) => cur.map((h) => (h.domain === domain ? merged : h)));
        pushToast({
          kind: "success",
          text:
            delta > 0
              ? `Boosted ${domain} (priority now ${merged.bias > 0 ? "+" : ""}${merged.bias}). Your tally: ${total > 0 ? "+" : ""}${total}, saved in this browser.`
              : `Penalized ${domain} (priority now ${merged.bias}). Your tally: ${total > 0 ? "+" : ""}${total}, saved in this browser.`,
        });
      } catch (err) {
        pushToast({ kind: "error", text: (err as Error).message });
      }
    },
    [pushToast, scheduleReorder],
  );

  const handlePrefer = useCallback(
    (domain: string) => {
      const next = preferred === domain ? null : domain;
      setPreferredInstance(next);
      setPreferred(next);
      pushToast({kind: "success", text: next ? `${domain} is now your preferred instance. The instance resolver will use ${domain} instance as long as it is considered healthy.` : `Removed ${domain} as preferred instance.` });
    },
    [preferred, pushToast],
  );

  // Initial load + auto refresh every 30s
  useEffect(() => {
    refresh();
    timer.current = window.setInterval(() => refresh(true), 30_000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [refresh]);

  const preferredHost = useMemo<HostEntry | null>(
    () => hosts.find((h) => h.domain === preferred) ?? null,
    [hosts, preferred],
  );

  // Filter + sort
  const visible = useMemo(() => {
    const filtered = hosts.filter((h) => {
      if (filter === "healthy" && !h.healthy) return false;
      if (filter === "unhealthy" && h.healthy) return false;
      if (onlyRss && !h.rss) return false;
      if (
        query &&
        !h.domain.toLowerCase().includes(query.toLowerCase())
      )
        return false;
      return true;
    });
    // While a Boost/Penalize reorder is pending, hold the display in the
    // frozen pre-adjustment order; sort positions apply once it lifts.
    if (frozenOrder) {
      const pos = new Map(frozenOrder.map((d, i) => [d, i]));
      return filtered.sort(
        (a, b) =>
          (pos.get(a.domain) ?? Number.MAX_SAFE_INTEGER) -
          (pos.get(b.domain) ?? Number.MAX_SAFE_INTEGER),
      );
    }
    return filtered.sort(compareHosts);
  }, [hosts, filter, onlyRss, query, frozenOrder, compareHosts]);

  return (
    <div class="app">
      <Header
        lastPoll={lastPoll}
        onPoll={handlePoll}
        refreshing={refreshing}
        onOpenSettings={() => setSettingsOpen(true)}
        preferred={preferred}
      />
      {/* "Keep Nitter alive!" funding pledge — sticky under the header;
          remounted per page so the session dismissal resets on navigation. */}
      <DonationBanner />
      <main class="container">
        {/* The search bar is the focal point of the landing page.
            The dropdown lets the user pick a healthy instance directly,
            or they can submit a path/handle/URL — honoring the preferred
            instance and the x.com redirect setting. */}
        <BrowseBar hosts={hosts} preferredHost={preferredHost} />

        <Toolbar
          query={query}
          onQuery={(q) => {
            cancelReorder();
            setQuery(q);
          }}
          filter={filter}
          onFilter={(f) => {
            cancelReorder();
            setFilter(f);
          }}
          sort={sort}
          onSort={(s) => {
            cancelReorder();
            setSort(s);
          }}
          onlyRss={onlyRss}
          onOnlyRss={(b) => {
            cancelReorder();
            setOnlyRss(b);
          }}
          count={visible.length}
        />

        {/* Persistent embed caveat (mirrored on the /view toolbar — wide —
            and in its hamburger menu — narrow): a healthy instance may still refuse to be embedded, so a broken-looking page is not proof of a broken instance. */}
        <p class="embed-note">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 1 0 0-20" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          <span>
Some instances disallow embedding. They may display incorrectly even though the instance is healthy. Open the instance in new tab to know for sure.
          </span>
        </p>

        {loading ? (
          <div class="empty">
            <div class="empty__icon">·</div>
            <div class="empty__title">Loading hosts…</div>
          </div>
        ) : visible.length === 0 ? (
          <div class="empty">
            <div class="empty__icon">∅</div>
            <div class="empty__title">No hosts match your filters</div>
            <p class="empty__text">
              Try clearing the search, switching the health filter, or
              running a manual poll.
            </p>
          </div>
        ) : (
          <div class="rows" role="list" ref={rowsRef}>
            {/* Quiet column labels (desktop only, aria-hidden — the rows
                themselves are self-describing). Uses the exact same grid
                template as .row so every label sits on its column. */}
            <div class="rows-head" aria-hidden="true">
              <span class="rows-head__score">Score</span>
              <span>Instance</span>
              <span class="rows-head__ping">Ping</span>
              <span class="rows-head__polls">Polls</span>
              <span class="rows-head__lastok">Last ok</span>
              <span>Priority</span>
              <span />
            </div>
            {visible.map((h) => (
              <HostRow
                key={h.domain}
                host={h}
                preferred={preferred}
                myBiasTotal={myBias[h.domain] ?? 0}
                onBoost={handleAdjust}
                onPenalize={handleAdjust}
                onPrefer={handlePrefer}
              />
            ))}
          </div>
        )}

        <hr class="instances-ruler"/>
        <p class="storage-note">
Ranking scores are computed on the server and shared across visitors. Your
Boost/Penalize adjustments are also saved in this browser and merged into the
priority you see — your nudges survive server-side drift. You may customize
your preferences in <a onClick={(e) => {
            e.preventDefault();
            navigate("/settings");
        }} href="/settings">settings</a> — the gear button opens them as a
popup, this link takes you to the dedicated /settings page.
        </p>
      </main>
      {settingsOpen && (
        <SettingsModal
          onClose={() => setSettingsOpen(false)}
          redirectX={redirectX}
          onRedirectX={(on) => {
            setRedirectX(on);
            setRedirectXState(on);
            pushToast({ kind: "success", text: on
                ? "<strong>X.com redirect ON:</strong> /v links now go straight to x.com."
                : "<strong>X.com redirect OFF:</strong> /v links use Nitter instances again.",
            });
          }}
          customSource={customSource}
          onCustomSource={(url) => {
            setCustomSource(url);
            setCustomSourceState(url);
          }}
          preferred={preferred}
          onClearPreferred={() => {
            setPreferredInstance(null);
            setPreferred(null);
          }}
          onToast={pushToast}
          onStorageChanged={syncFromStorage}
        />
      )}
      <ToastHost toasts={toasts} />
    </div>
  );
}

render(<App />, document.getElementById("app")!);
